import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import { parseMetaName } from '../../../Helpers';
import { get, postJson, putJson, del } from '../../../hook/request';

type OreLavorativeRow = {
  ID: number;
  anno: number;
  qtaOra: number;
  tipoOre: number;
};

type CategoryApi = {
  ID: number;
  meta?: unknown;
  codice?: string;
  ordine?: number;
  abilitato?: 0 | 1;
};

type CategoryOption = {
  id: number;
  label: string;
};

const ENDPOINT = '/users/ore_lavorative';

type Props = {
  userID: number;
};

function extractArray(value: any): any[] {
  const root = value?.data ?? value;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  return [];
}

function normalizeRows(raw: any[]): OreLavorativeRow[] {
  return raw.map(row => ({
    ID: Number(row.ID ?? row.id ?? 0),
    anno: Number(row.anno ?? 0),
    qtaOra: Number(
      row.tot_ore ?? row.qtaOra ?? row.qta ?? row.quantita_ore ?? 0
    ),
    tipoOre: Number(
      row.tipo_ore ??
        row.tipoOre ??
        row.typeID ??
        row.categoryID ??
        row.tipoID ??
        0
    ),
  }));
}

export default function OreLavorative({ userID }: Props) {
  const [open, setOpen] = useState<'oreLavorative' | ''>('oreLavorative');
  const [loading, setLoading] = useState(true);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [deletingRow, setDeletingRow] = useState<number | null>(null);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [rows, setRows] = useState<OreLavorativeRow[]>([]);

  const [newAnno, setNewAnno] = useState(String(new Date().getFullYear()));
  const [newQtaOra, setNewQtaOra] = useState('');
  const [newTipoOre, setNewTipoOre] = useState('');

  const loadRows = async () => {
    const response = await get(
      `${ENDPOINT}?userID=${encodeURIComponent(String(userID))}`
    );
    setRows(normalizeRows(extractArray(response)));
  };

  useEffect(() => {
    (async () => {
      try {
        const [categoryResponse, rowsResponse] = await Promise.all([
          get<CategoryApi[] | { data?: CategoryApi[] }>(
            '/categories/parent?ID=698'
          ),
          get(`${ENDPOINT}?userID=${encodeURIComponent(String(userID))}`),
        ]);

        const options = extractArray(categoryResponse)
          .filter(category => (category.abilitato ?? 1) === 1)
          .sort(
            (a, b) => Number(a.ordine ?? 0) - Number(b.ordine ?? 0)
          )
          .map(category => ({
            id: Number(category.ID),
            label:
              parseMetaName(category.meta, 'it') ||
              category.codice ||
              String(category.ID),
          }));

        setCategories(options);
        setRows(normalizeRows(extractArray(rowsResponse)));
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ??
            error?.message ??
            'Errore nel caricamento delle ore lavorative'
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [userID]);

  const buildPayload = (anno: number, qtaOra: number, tipoOre: number) => ({
    userID,
    anno,
    qta_ora: qtaOra,
    tipo_ore: tipoOre,
    user_email: localStorage.getItem('email') || 'supporto@i-factory.biz',
  });

  const handleAdd = async () => {
    const anno = Number(newAnno);
    const qtaOra = Number(newQtaOra);
    const tipoOre = Number(newTipoOre);

    if (!Number.isInteger(anno) || anno <= 0 || qtaOra <= 0 || !tipoOre) {
      toast.warn('Inserisci anno, quantità ore e tipo ore');
      return;
    }

    try {
      setSavingRow(-1);
      await postJson(ENDPOINT, buildPayload(anno, qtaOra, tipoOre));
      await loadRows();
      setNewAnno(String(new Date().getFullYear()));
      setNewQtaOra('');
      setNewTipoOre('');
      toast.success('Ore lavorative aggiunte');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore durante l’aggiunta'
      );
    } finally {
      setSavingRow(null);
    }
  };

  const handleUpdate = async (row: OreLavorativeRow) => {
    if (
      !Number.isInteger(row.anno) ||
      row.anno <= 0 ||
      row.qtaOra <= 0 ||
      !row.tipoOre
    ) {
      toast.warn('Inserisci anno, quantità ore e tipo ore');
      return;
    }

    try {
      setSavingRow(row.ID);
      await putJson(ENDPOINT, {
        id: row.ID,
        ...buildPayload(row.anno, row.qtaOra, row.tipoOre),
      });
      toast.success('Ore lavorative aggiornate');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore durante l’aggiornamento'
      );
    } finally {
      setSavingRow(null);
    }
  };


    const handleDelete = async (row: OreLavorativeRow) => {
      if (!window.confirm('Eliminare questa ora lavorativa?')) return;
  
      try {
        setDeletingRow(row.ID);
        await del(ENDPOINT+`?ID=${encodeURIComponent(String(row.ID))}`);
        setRows(prev => prev.filter(r => r.ID !== row.ID));
        toast.success('Ora Lavorativa eliminata');
      } catch (e: any) {
        toast.error(
          e?.response?.data?.message ?? e?.message ?? 'Errore eliminazione'
        );
      } finally {
        setDeletingRow(null);
      }
    };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner />
      </div>
    );
  }

  return (
    <Accordion
      open={open}
      toggle={() =>
        setOpen(current => (current === 'oreLavorative' ? '' : 'oreLavorative'))
      }
      className="icons-accordion w-100"
    >
      <AccordionItem>
        <AccordionHeader targetId="oreLavorative">
          <span className="font-secondary">Ore lavorative</span>
        </AccordionHeader>

        <AccordionBody accordionId="oreLavorative">
          <Row className="g-3 align-items-end mb-4">
            <Col md={3}>
              <Label>Anno</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={newAnno}
                onChange={event => setNewAnno(event.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label>Qta tempo</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={newQtaOra}
                onChange={event => setNewQtaOra(event.target.value)}
              />
            </Col>
            <Col md={4}>
              <Label>Tipo ore</Label>
              <Input
                type="select"
                value={newTipoOre}
                onChange={event => setNewTipoOre(event.target.value)}
              >
                <option value="">Seleziona...</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Btn
                color="success"
                className="text-white"
                type="button"
                onClick={handleAdd}
                disabled={savingRow === -1}
              >
                Aggiungi
              </Btn>
            </Col>
          </Row>

          {rows.length === 0 && (
            <div className="text-muted">Nessuna ora lavorativa disponibile.</div>
          )}

          {rows.map(row => (
            <Row key={row.ID} className="g-3 align-items-end mb-3">
              <Col md={3}>
                <Label>Anno</Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={row.anno}
                  onChange={event =>
                    setRows(current =>
                      current.map(item =>
                        item.ID === row.ID
                          ? { ...item, anno: Number(event.target.value) }
                          : item
                      )
                    )
                  }
                />
              </Col>
              <Col md={3}>
                <Label>Qta tempo</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={row.qtaOra}
                  onChange={event =>
                    setRows(current =>
                      current.map(item =>
                        item.ID === row.ID
                          ? { ...item, qtaOra: Number(event.target.value) }
                          : item
                      )
                    )
                  }
                />
              </Col>
              <Col md={4}>
                <Label>Tipo ore</Label>
                <Input
                  type="select"
                  value={row.tipoOre}
                  onChange={event =>
                    setRows(current =>
                      current.map(item =>
                        item.ID === row.ID
                          ? { ...item, tipoOre: Number(event.target.value) }
                          : item
                      )
                    )
                  }
                >
                  <option value="">Seleziona...</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={2}>
                <Btn
                  color="info"
                  className="text-white"
                  type="button"
                  onClick={() => handleUpdate(row)}
                  disabled={savingRow === row.ID}
                >
                  Aggiorna
                </Btn>
                  <Btn
                  color="danger"
                  className="text-white ms-2"
                  onClick={() => handleDelete(row)}
                  disabled={deletingRow === row.ID}
                >
                  Elimina
                </Btn>
              </Col>
            </Row>
          ))}
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );
}
